/**
 * Subscription detail page with billing history and cancellation.
 */

import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api, ApiRequestError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/useToast'
import { formatCurrency, formatDate, formatDateTime, getStatusConfig } from '@/lib/formatters'
import type { Subscription, SubscriptionPeriod } from '@/hooks/useSubscriptions'

export default function SubscriptionDetailPage() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const fetchSubscription = async () => {
    if (!subscriptionId) return

    try {
      setLoading(true)
      const response = await api.get<Subscription>(`/subscriptions/${subscriptionId}`)
      setSubscription(response.data)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to load subscription')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSubscription()
  }, [subscriptionId])

  const handleCancel = async () => {
    setIsSubmitting(true)
    try {
      await api.post(`/subscriptions/${subscriptionId}/cancel`, {
        reason: cancelReason || undefined,
      })
      toast({ title: 'Success', description: 'Subscription cancelled successfully', variant: 'success' })
      setIsCancelOpen(false)
      fetchSubscription()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof ApiRequestError ? err.message : 'Failed to cancel subscription',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (error || !subscription) {
    return (
      <div>
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/subscriptions">Back to Subscriptions</Link>
        </Button>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error || 'Subscription not found'}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={fetchSubscription}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const statusConfig = getStatusConfig(subscription.status)
  const billingCycleLabels: Record<string, string> = {
    monthly: 'Monthly',
    yearly: 'Yearly',
    weekly: 'Weekly',
  }

  return (
    <div>
      <Button variant="ghost" asChild className="mb-4">
        <Link to="/subscriptions">Back to Subscriptions</Link>
      </Button>

      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold">Subscription {subscription.subscription_no}</h1>
          <p className="text-muted-foreground mt-2">
            Started {formatDate(subscription.created_at)}
          </p>
        </div>
        <Badge className={statusConfig.className} style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
          {statusConfig.label}
        </Badge>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Subscription Info */}
        <Card>
          <CardHeader>
            <CardTitle>Subscription Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subscription No</span>
              <span className="font-mono">{subscription.subscription_no}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Product</span>
              <span>{subscription.product_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Plan</span>
              <span>{subscription.sku_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-semibold">
                {formatCurrency(subscription.amount, subscription.currency)}/{subscription.billing_cycle}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Billing Cycle</span>
              <span>{billingCycleLabels[subscription.billing_cycle] || subscription.billing_cycle}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <Badge className={statusConfig.className}>{statusConfig.label}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Customer Info */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name</span>
              <span>{subscription.customer_name || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email</span>
              <span>{subscription.customer_email}</span>
            </div>
          </CardContent>
        </Card>

        {/* Billing Period */}
        <Card>
          <CardHeader>
            <CardTitle>Current Billing Period</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Period Start</span>
              <span>{formatDate(subscription.current_period_start)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Period End</span>
              <span>{formatDate(subscription.current_period_end)}</span>
            </div>
            {subscription.next_billing_date && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Next Billing</span>
                <span>{formatDate(subscription.next_billing_date)}</span>
              </div>
            )}
            {subscription.cancelled_at && (
              <>
                <div className="flex justify-between text-destructive">
                  <span>Cancelled At</span>
                  <span>{formatDateTime(subscription.cancelled_at)}</span>
                </div>
                {subscription.cancel_reason && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cancel Reason</span>
                    <span>{subscription.cancel_reason}</span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
            <CardDescription>Manage this subscription</CardDescription>
          </CardHeader>
          <CardContent>
            {subscription.status === 'active' && (
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => setIsCancelOpen(true)}
              >
                Cancel Subscription
              </Button>
            )}
            {subscription.status !== 'active' && (
              <p className="text-muted-foreground text-sm">
                No actions available for {subscription.status} subscriptions.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Billing History */}
        {subscription.periods && subscription.periods.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Billing History</CardTitle>
              <CardDescription>Past billing periods and payments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {subscription.periods.map((period: SubscriptionPeriod) => {
                  const periodStatus = getStatusConfig(period.status)
                  return (
                    <div
                      key={period.id}
                      className="flex items-center justify-between p-3 border rounded"
                    >
                      <div>
                        <div className="font-medium">
                          {formatDate(period.period_start)} - {formatDate(period.period_end)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {period.payment_id ? `Payment: ${period.payment_id}` : 'No payment'}
                        </div>
                      </div>
                      <Badge className={periodStatus.className}>{periodStatus.label}</Badge>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Cancel Dialog */}
      <Dialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              The subscription will be cancelled at the end of the current billing period
              ({formatDate(subscription.current_period_end)}). The customer will retain access until then.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Reason (optional)</label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation..."
              className="mt-2 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCancelOpen(false)}>
              Keep Subscription
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={isSubmitting}>
              {isSubmitting ? 'Cancelling...' : 'Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
